import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import { Card } from "../ui/Card";
import { Modal } from "../ui/Modal";
import { Badge } from "../ui/Badge";
import type {
  SubmissionDTO,
  SubmissionOcrMetadataRubricDTO,
  SubmissionOcrMetadataSuggestionDTO,
} from "@/modules/learning/api/assignments";
import {
  coerceValidationStaffRubric,
  publishReview,
  publishReviewWithCorrection,
} from "@/modules/learning/api/assignments";
import {
  TeacherImageCorrectionEditor,
  type TeacherImageCorrectionEditorRef,
} from "./TeacherImageCorrectionEditor";
import { showSuccessToast, showErrorToast } from "@/utils/errorHandler";

export interface ReviewSubmissionContentProps {
  submission: SubmissionDTO;
  onPublished: () => void;
}

function normalizeSuggestions(
  raw: unknown
): SubmissionOcrMetadataSuggestionDTO[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (item): item is SubmissionOcrMetadataSuggestionDTO =>
      item != null && typeof item === "object"
  );
}

function hasRubricDisplayContent(
  rubric: SubmissionOcrMetadataRubricDTO | null | undefined
): boolean {
  if (!rubric || typeof rubric !== "object") return false;
  const nums = [
    rubric.overallScore,
    rubric.grammar,
    rubric.clarity,
    rubric.vocabulary,
  ];
  if (nums.some((n) => n != null && Number.isFinite(Number(n)))) return true;
  const fb = rubric.feedback;
  return typeof fb === "string" && fb.trim().length > 0;
}

function formatStatusLabel(status: string | undefined): string {
  if (!status?.trim()) return "—";
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export const ReviewSubmissionContent: React.FC<ReviewSubmissionContentProps> = ({
  submission,
  onPublished,
}) => {
  const imageUrl = submission.image_url?.trim() ?? "";
  const correctionImageUrl = submission.correction_image_url?.trim() ?? "";
  const hasPublishedCorrection = Boolean(correctionImageUrl);

  const [grade, setGrade] = useState<number>(0);
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [correctionLightboxOpen, setCorrectionLightboxOpen] = useState(false);
  const correctionEditorRef = useRef<TeacherImageCorrectionEditorRef>(null);

  /** Editor still reports readiness for internal tooling; publish does not depend on it. */
  const onEditorImageReadyChange = useCallback((_ready: boolean) => {}, []);

  useEffect(() => {
    setGrade(submission.grade ?? submission.ocr_score ?? 0);
    setFeedback(submission.feedback ?? "");
  }, [submission]);

  useEffect(() => {
    setCorrectionLightboxOpen(false);
  }, [submission.submission_id, correctionImageUrl]);

  const suggestions = useMemo(
    () => normalizeSuggestions(submission.validation_staff?.suggestions),
    [submission.validation_staff?.suggestions]
  );

  const rubric = coerceValidationStaffRubric(submission.validation_staff);
  const rubricHasContent = hasRubricDisplayContent(rubric);
  const rubricOverall = rubric?.overallScore;

  const handlePublish = async () => {
    setLoading(true);
    try {
      const hasHandwrittenImage = Boolean(imageUrl);
      const annotationPayload = hasHandwrittenImage
        ? correctionEditorRef.current?.getAnnotationPayload() ?? undefined
        : undefined;

      if (hasHandwrittenImage && annotationPayload != null) {
        const editor = correctionEditorRef.current;
        if (!editor) {
          showErrorToast("Correction editor is not ready.");
          return;
        }
        const filename = `${submission.submission_id}-teacher-correction.png`;
        const file = await editor.exportMergedImage(filename);
        await publishReviewWithCorrection(submission.submission_id, {
          grade,
          feedback,
          file,
          annotationPayload,
        });
      } else {
        await publishReview(submission.submission_id, { grade, feedback });
      }
      showSuccessToast("Review published. Student can now see their score and feedback.");
      onPublished();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : "Failed to publish.");
    } finally {
      setLoading(false);
    }
  };

  const ocrScore = submission.ocr_score;
  const isPublished = submission.status?.toLowerCase() === "published";
  const showOcrScoreInSummary = ocrScore != null;
  const showRubricOverallInSummary =
    rubricOverall != null && Number.isFinite(Number(rubricOverall));

  return (
    <div className="min-h-0">
      <div className="space-y-4 min-h-0 pb-28">
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between sm:gap-x-4 sm:gap-y-2 border-b border-slate-200 dark:border-slate-700 pb-4">
          <div className="min-w-0 flex-1">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Assignment:{" "}
              <span className="font-medium text-slate-900 dark:text-white">
                {submission.assignment_title || submission.assignment_id}
              </span>
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              <span className="text-slate-500 dark:text-slate-500">Student</span>
              {submission.student_display_name?.trim() ? (
                <>
                  :{" "}
                  <span className="font-medium text-slate-900 dark:text-white">
                    {submission.student_display_name.trim()}
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <div className="flex flex-col gap-1 text-sm sm:items-end sm:text-right shrink-0">
            <p className="text-slate-600 dark:text-slate-400">
              <span className="text-slate-500 dark:text-slate-500">Submission status: </span>
              <span className="font-medium text-slate-900 dark:text-white">
                {formatStatusLabel(submission.status)}
              </span>
            </p>
            {showOcrScoreInSummary && (
              <p className="text-slate-600 dark:text-slate-400">
                <span className="text-slate-500 dark:text-slate-500">OCR score: </span>
                <span className="font-semibold text-slate-900 dark:text-white tabular-nums">
                  {ocrScore}/100
                </span>
              </p>
            )}
            {showRubricOverallInSummary && (
              <p className="text-slate-600 dark:text-slate-400">
                <span className="text-slate-500 dark:text-slate-500">Rubric overall score: </span>
                <span className="font-semibold text-slate-900 dark:text-white tabular-nums">
                  {rubricOverall}
                </span>
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start min-h-0">
          {hasPublishedCorrection ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 min-h-0 min-w-0">
              <Card padding="md" className="min-h-0 min-w-0 overflow-hidden">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                  Original submission
                </p>
                {imageUrl ? (
                  <div className="space-y-2 min-w-0">
                    {submission.filename?.trim() ? (
                      <p
                        className="text-xs text-slate-600 dark:text-slate-400 truncate"
                        title={submission.filename}
                      >
                        File: {submission.filename}
                      </p>
                    ) : null}
                    <TeacherImageCorrectionEditor
                      key={`${submission.submission_id}:${imageUrl}`}
                      ref={correctionEditorRef}
                      imageUrl={imageUrl}
                      submissionId={submission.submission_id}
                      onImageReadyChange={onEditorImageReadyChange}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">
                    No handwritten image for this submission.
                  </p>
                )}
              </Card>
              <Card padding="md" className="min-h-0 min-w-0 overflow-hidden">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                  Published correction
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">
                  This is the correction image already published to the student.
                </p>
                <div className="flex justify-center rounded-lg bg-slate-50 dark:bg-slate-900/50 p-2 overflow-hidden">
                  <button
                    type="button"
                    className="inline-flex max-w-full cursor-zoom-in rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 dark:focus-visible:ring-offset-slate-900"
                    onClick={() => setCorrectionLightboxOpen(true)}
                    aria-label="View published correction full size"
                  >
                    <img
                      src={correctionImageUrl}
                      alt="Published correction overlay"
                      className="max-w-full w-auto max-h-[min(60vh,28rem)] h-auto object-contain select-none pointer-events-none"
                      draggable={false}
                    />
                  </button>
                </div>
              </Card>
            </div>
          ) : (
            <Card padding="md" className="min-h-0 min-w-0 overflow-hidden">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                Original submission
              </p>
              {imageUrl ? (
                <div className="space-y-2">
                  {submission.filename?.trim() ? (
                    <p
                      className="text-xs text-slate-600 dark:text-slate-400 truncate"
                      title={submission.filename}
                    >
                      File: {submission.filename}
                    </p>
                  ) : null}
                  <TeacherImageCorrectionEditor
                    key={`${submission.submission_id}:${imageUrl}`}
                    ref={correctionEditorRef}
                    imageUrl={imageUrl}
                    submissionId={submission.submission_id}
                    onImageReadyChange={onEditorImageReadyChange}
                  />
                </div>
              ) : (
                <p className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">
                  No handwritten image for this submission.
                </p>
              )}
            </Card>
          )}

          <div className="flex flex-col gap-4 min-h-0">
            <Card padding="md">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                Rubric (OCR)
              </p>
              {!rubricHasContent ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 py-2">
                  No rubric data from OCR.
                </p>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {rubric?.overallScore != null && Number.isFinite(Number(rubric.overallScore)) && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-medium text-slate-800 dark:text-slate-200">
                        Rubric overall score: {rubric.overallScore}
                      </span>
                    )}
                    {rubric?.grammar != null && Number.isFinite(Number(rubric.grammar)) && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-medium text-slate-800 dark:text-slate-200">
                        Grammar: {rubric.grammar}
                      </span>
                    )}
                    {rubric?.clarity != null && Number.isFinite(Number(rubric.clarity)) && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-medium text-slate-800 dark:text-slate-200">
                        Clarity: {rubric.clarity}
                      </span>
                    )}
                    {rubric?.vocabulary != null && Number.isFinite(Number(rubric.vocabulary)) && (
                      <span className="inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-medium text-slate-800 dark:text-slate-200">
                        Vocabulary: {rubric.vocabulary}
                      </span>
                    )}
                  </div>
                  {rubric?.feedback?.trim() ? (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-1">
                        OCR rubric notes
                      </p>
                      <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                        {rubric.feedback}
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
            </Card>

            <Card padding="md">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                Suggestions (OCR)
              </p>
              {suggestions.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 py-2">
                  No suggestions from OCR.
                </p>
              ) : (
                <div className="max-h-64 overflow-y-auto overflow-x-hidden pr-1 -mr-1">
                  <ul className="space-y-3 list-none p-0 m-0">
                    {suggestions.map((s, idx) => (
                      <li
                        key={idx}
                        className="rounded-lg border border-slate-200 dark:border-slate-600 p-3 text-sm"
                      >
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {s.tag?.trim() ? (
                            <Badge variant="gray" size="sm">
                              {s.tag}
                            </Badge>
                          ) : null}
                          {s.confidence != null && Number.isFinite(Number(s.confidence)) ? (
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              Confidence: {s.confidence}
                            </span>
                          ) : null}
                        </div>
                        {(s.original != null && String(s.original).length > 0) ||
                        (s.suggestion != null && String(s.suggestion).length > 0) ? (
                          <p className="text-slate-800 dark:text-slate-200">
                            {s.original != null && String(s.original).length > 0 ? (
                              <span className="text-slate-600 dark:text-slate-400">Original: </span>
                            ) : null}
                            {s.original != null ? String(s.original) : ""}
                            {s.original != null &&
                            String(s.original).length > 0 &&
                            s.suggestion != null &&
                            String(s.suggestion).length > 0 ? (
                              <span className="mx-1 text-slate-400">→</span>
                            ) : null}
                            {s.suggestion != null && String(s.suggestion).length > 0 ? (
                              <>
                                <span className="text-slate-600 dark:text-slate-400">Suggestion: </span>
                                {String(s.suggestion)}
                              </>
                            ) : null}
                          </p>
                        ) : null}
                        {s.reason_tamil?.trim() ? (
                          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">
                            {s.reason_tamil}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Card>

            <Card padding="md">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                Teacher review
              </p>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="teacher-grade-input"
                    className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >
                    Teacher grade (0–100)
                  </label>
                  <Input
                    id="teacher-grade-input"
                    type="number"
                    min={0}
                    max={100}
                    value={grade}
                    onChange={(e) => setGrade(parseInt(e.target.value, 10) || 0)}
                  />
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    This is your official grade for the student; it is saved when you publish.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Feedback for student
                  </label>
                  <Textarea
                    rows={4}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="Add feedback for the student..."
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 mt-6 border-t border-slate-200 bg-app-bg/95 py-4 backdrop-blur-sm dark:border-slate-700 dark:bg-gray-950/95">
        <div className="mx-auto max-w-6xl">
          <Button
            type="button"
            onClick={handlePublish}
            disabled={loading || isPublished}
            className="w-full bg-primary-600 hover:bg-primary-700 !text-white sm:max-w-md"
          >
            {isPublished ? "Already Published" : loading ? "Publishing..." : "Publish Review"}
          </Button>
        </div>
      </div>

      <Modal
        isOpen={correctionLightboxOpen && Boolean(correctionImageUrl)}
        onClose={() => setCorrectionLightboxOpen(false)}
        title="Published correction"
        size="xl"
        showCloseButton
      >
        <div className="flex min-h-0 justify-center">
          <img
            src={correctionImageUrl || undefined}
            alt="Published correction full size"
            className="max-h-[min(78vh,56rem)] w-auto max-w-full object-contain"
            draggable={false}
          />
        </div>
      </Modal>
    </div>
  );
};
